import { useEffect, useState, createContext, useContext, type ReactNode } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate, useParams, useLocation } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles,
  Clapperboard,
  UsersRound,
  BookOpen,
  Brain,
  Layers3,
  SlidersHorizontal,
  ChevronDown,
  Plus,
  ArrowUpRight,
  ArrowRight,
  MessageSquare,
  Bell,
  CircleHelp,
  Check,
  LogOut,
  X,
  Menu,
  CircleDot,
  Film,
  Trash2,
} from 'lucide-react';
import { api, session, login, type Schema, type Page } from '../api/client';
import { useApi, Dialog, Field, Spinner, Badge, MoonScene } from './shared';
import {
  StudioPage,
  EpisodesPage,
  CharactersPage,
  StoryPage,
  MemoryPage,
  ModelsPage,
  JobsPage,
} from './pages';

import { ProductionPage } from './production';
import { NovelPage } from './NovelPage';
import { DeleteSeriesDialog, SeriesTrashDialog } from './SeriesTrash';

type StudioContext = {
  workspaceId: string;
  base: string;
  project: Schema<'Series'> | undefined;
  projects: Schema<'Series'>[];
  branch: Schema<'Branch'> | undefined;
  baselines: Schema<'ArtifactVersion'>[];
  episodes: Schema<'Episode'>[];
  episodesLoading: boolean;
  toast: (message: string, error?: boolean) => void;
  refresh: () => Promise<void>;
  go: (view: string) => void;
  busy: boolean;
  perform: (fn: () => Promise<unknown>, success?: string) => Promise<void>;
};
const Context = createContext<StudioContext | null>(null);
export function useStudio() {
  const value = useContext(Context);
  if (!value) throw new Error('Studio context missing');
  return value;
}
export function App() {
  const query = useQuery({ queryKey: ['session'], queryFn: session, retry: false });
  if (query.isPending)
    return (
      <div className="loading-page">
        <Brand />
        <Spinner />
      </div>
    );
  if (query.isError)
    return (
      <div className="loading-page">
        <Brand />
        <h2>工作室暂时未连接</h2>
        <p>服务暂时不可用，请稍后重新连接。</p>
        <button className="button primary" onClick={() => void query.refetch()}>
          重新连接
        </button>
      </div>
    );
  if (!query.data.authenticated) return <Login />;
  const workspace = query.data.workspaceIds?.[0];
  if (!workspace) return <div className="loading-page">当前账号没有工作区。</div>;
  return (
    <Routes>
      <Route path="/" element={<Navigate replace to={`/w/${workspace}/projects`} />} />
      <Route path="/w/:workspaceId" element={<Shell />}>
        <Route path="series/:seriesId/:view" element={<PageRoute />} />
        <Route path=":view/*" element={<PageRoute />} />
        <Route index element={<Navigate to="projects" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
export function Brand() {
  return (
    <div className="brand">
      <span className="brand-mark">
        <i />
        <i />
      </span>
      <b>
        dream<span>®</span>
      </b>
    </div>
  );
}
function Login() {
  const [email, setEmail] = useState('creator@dream.local'),
    [password, setPassword] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  return (
    <div className="login-layout">
      <div className="login-art">
        <Brand />
        <div>
          <span className="eyebrow">YOUR STORY, CONTINUED.</span>
          <h1>
            让故事发生。
            <br />
            让它继续。
          </h1>
          <p>与 AI 一起，创作属于你的连续短剧。</p>
        </div>
        <MoonScene />
        <footer>Dream Studio · 创作的每一步，都有来由。</footer>
      </div>
      <section className="login-form">
        <div className="login-inner">
          <span className="eyebrow">WELCOME BACK</span>
          <h2>进入你的工作室</h2>
          <p>故事、角色和每一次决定，都在这里等你。</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setBusy(true);
              setError('');
              void login(email, password)
                .then((data) => qc.setQueryData(['session'], data))
                .catch((e) => setError((e as Error).message))
                .finally(() => setBusy(false));
            }}
          >
            <Field label="邮箱">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </Field>
            <Field label="密码">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button className="button primary full" disabled={busy}>
              {busy ? (
                <Spinner />
              ) : (
                <>
                  进入工作室 <ArrowRight size={17} />
                </>
              )}
            </button>
          </form>
          <div className="local-hint">
            <CircleDot size={14} />
            <span>使用工作室账号登录。登录信息由工作室管理员提供。</span>
          </div>
        </div>
      </section>
    </div>
  );
}
const nav = [
  { key: 'novel', name: '小说改编 Agent', icon: BookOpen },
  { key: 'studio', name: '创作工作台', icon: Sparkles },
  { key: 'episodes', name: '剧集', icon: Clapperboard },
  { key: 'production', name: '制作与成片', icon: Film },
  { key: 'characters', name: '角色', icon: UsersRound },
  { key: 'story', name: '故事圣经', icon: BookOpen },
  { key: 'memory', name: '连续性记忆', icon: Brain },
];
function Shell() {
  const { workspaceId = '', seriesId } = useParams();
  const location = useLocation(),
    navigate = useNavigate(),
    qc = useQueryClient();
  const base = `/workspaces/${workspaceId}`;
  const projectsQuery = useApi<Page<Schema<'Series'>>>(`${base}/series`);
  const projects = projectsQuery.data?.items ?? [];
  const activeId = seriesId ?? projects[0]?.id;
  const projectQuery = useApi<Schema<'Series'>>(`${base}/series/${activeId ?? ''}`, !!activeId);
  const project = projectQuery.data;
  useEffect(() => {
    if (
      seriesId &&
      projectQuery.isError &&
      projectsQuery.isSuccess &&
      !projects.some((p) => p.id === seriesId)
    ) {
      navigate(`/w/${workspaceId}/projects`, { replace: true });
    }
  }, [seriesId, projectQuery.isError, projectsQuery.isSuccess, projects, workspaceId, navigate]);
  const branches = useApi<Page<Schema<'Branch'>>>(
    `${base}/series/${activeId ?? ''}/branches`,
    !!activeId,
  );
  const [branchChoices, setBranchChoices] = useState<Record<string, string>>({});
  const [branchDialog, setBranchDialog] = useState(false),
    [branchName, setBranchName] = useState('');
  const requestedBranch = new URLSearchParams(location.search).get('branch') ?? '';
  const selectedBranch =
    requestedBranch ||
    (branchChoices[activeId ?? ''] ??
      sessionStorage.getItem('dream.branch.' + activeId) ??
      project?.defaultBranchId);
  const branch =
    branches.data?.items.find((b) => b.id === selectedBranch) ??
    (!requestedBranch
      ? branches.data?.items.find((b) => b.id === project?.defaultBranchId)
      : undefined);
  useEffect(() => {
    if (
      activeId &&
      requestedBranch &&
      branches.data?.items.some((item) => item.id === requestedBranch)
    ) {
      sessionStorage.setItem('dream.branch.' + activeId, requestedBranch);
      setBranchChoices((old) =>
        old[activeId] === requestedBranch ? old : { ...old, [activeId]: requestedBranch },
      );
    }
  }, [activeId, requestedBranch, branches.data]);
  const baselineQuery = useApi<Page<Schema<'ArtifactVersion'>>>(
    `${base}/branches/${branch?.id}/baseline`,
    !!branch,
  );
  const selectBranch = (id: string) => {
    if (activeId) {
      sessionStorage.setItem('dream.branch.' + activeId, id);
      setBranchChoices({ ...branchChoices, [activeId]: id });
      const params = new URLSearchParams(location.search);
      params.set('branch', id);
      params.delete('episode');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  };
  const episodesQuery = useApi<Page<Schema<'Episode'>>>(
    `${base}/series/${activeId ?? ''}/episodes?branchId=${branch?.id ?? ''}`,
    !!activeId && !!branch,
  );
  const episodes = episodesQuery.data?.items ?? [];
  const [connectionState, setConnectionState] = useState<
    'connecting' | 'connected' | 'disconnected'
  >('connecting');
  const [notice, setNotice] = useState<{ message: string; error: boolean } | null>(null),
    [showCreate, setShowCreate] = useState(false),
    [showProjects, setShowProjects] = useState(false),
    [mobile, setMobile] = useState(false),
    [busy, setBusy] = useState(false),
    [help, setHelp] = useState(false);
  function toast(message: string, error = false) {
    setNotice({ message, error });
  }
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [notice]);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['api'] });
  };
  const perform = async (fn: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
      if (success) toast(success);
    } catch (e) {
      toast((e as Error).message, true);
    } finally {
      setBusy(false);
    }
  };
  const go = (view: string) => {
    setMobile(false);
    if (view === 'models' && seriesId) {
      navigate(`/w/${workspaceId}/series/${seriesId}/models`);
      return;
    }
    if (['projects', 'models', 'jobs', 'novels'].includes(view))
      navigate(`/w/${workspaceId}/${view}`);
    else if (activeId) navigate(`/w/${workspaceId}/series/${activeId}/${view}`);
    else setShowCreate(true);
  };
  useEffect(() => {
    const stream = new EventSource(`/api/v1${base}/events`, { withCredentials: true });
    setConnectionState('connecting');
    stream.onopen = () => setConnectionState('connected');
    stream.onerror = () => setConnectionState('disconnected');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const invalidate = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void qc.invalidateQueries({ queryKey: ['api'] }), 150);
    };
    stream.addEventListener('message.delta', () => {
      void qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'api' &&
          typeof q.queryKey[1] === 'string' &&
          q.queryKey[1].startsWith(`${base}/conversations/`) &&
          q.queryKey[1].endsWith('/messages'),
      });
    });
    for (const type of [
      'message.completed',
      'message.failed',
      'agent_run.updated',
      'approval.created',
      'artifact.invalidated',
      'job.updated',
      'release.created',
      'series.deleted',
      'series.restored',
    ])
      stream.addEventListener(type, invalidate);
    const resync = () => {
      stream.close();
      invalidate();
    };
    stream.addEventListener('resync_required', resync);
    return () => {
      stream.close();
      if (timer) clearTimeout(timer);
    };
  }, [base, qc]);
  const current = location.pathname.split('/').filter(Boolean).pop() ?? 'projects';
  const title =
    nav.find((n) => n.key === current)?.name ??
    { projects: '我的系列', models: '模型与连接', jobs: '任务中心', novels: '小说改编 Agent' }[
      current
    ] ??
    '工作室';
  const context: StudioContext = {
    workspaceId,
    base,
    project,
    projects,
    branch,
    baselines: baselineQuery.data?.items ?? [],
    episodes,
    episodesLoading: episodesQuery.isFetching || branches.isPending,
    toast,
    refresh,
    go,
    busy,
    perform,
  };
  return (
    <Context.Provider value={context}>
      <div className="app-layout">
        <aside className={`sidebar ${mobile ? 'open' : ''}`}>
          <Brand />
          <button className="workspace-switch" onClick={() => setShowProjects(!showProjects)}>
            <span className="workspace-avatar">D</span>
            <span>
              <strong>我的工作室</strong>
              <small>个人创作空间</small>
            </span>
            <ChevronDown size={14} />
          </button>
          {showProjects && (
            <div className="project-switch-list">
              <button
                onClick={() => {
                  go('projects');
                  setShowProjects(false);
                }}
              >
                查看全部系列
              </button>
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    navigate(`/w/${workspaceId}/series/${p.id}/studio`);
                    setShowProjects(false);
                  }}
                >
                  {p.title}
                </button>
              ))}
            </div>
          )}
          <button className="new-project" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> 新建系列
          </button>
          {project && branch && (
            <div className="branch-switch">
              <select
                aria-label="故事分支"
                value={branch.id}
                onChange={(e) => selectBranch(e.target.value)}
              >
                {branches.data?.items.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <button
                className="icon-button"
                title="创建故事分支"
                aria-label="创建故事分支"
                onClick={() => {
                  setBranchName('');
                  setBranchDialog(true);
                }}
              >
                <Plus size={14} />
              </button>
            </div>
          )}
          <div className="nav-label">你的故事</div>
          <nav>
            {nav.map((item) => (
              <button
                key={item.key}
                className={current === item.key ? 'active' : ''}
                onClick={() => go(item.key)}
              >
                <item.icon size={18} />
                {item.name}
                {item.key === 'episodes' && episodes.length > 0 && (
                  <span className="nav-count">{episodes.length}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="nav-divider" />
          <div className="nav-label">工作室</div>
          <nav>
            <button
              className={current === 'projects' ? 'active' : ''}
              onClick={() => go('projects')}
            >
              <Layers3 size={18} />
              全部系列
            </button>
            <button className={current === 'novels' ? 'active' : ''} onClick={() => go('novels')}>
              <BookOpen size={18} />
              小说改编
            </button>
            <button className={current === 'jobs' ? 'active' : ''} onClick={() => go('jobs')}>
              <Film size={18} />
              任务中心
            </button>
            <button className={current === 'models' ? 'active' : ''} onClick={() => go('models')}>
              <SlidersHorizontal size={18} />
              模型与连接
            </button>
          </nav>
          <div className="sidebar-bottom">
            <div className="studio-note">
              <div className="tiny-spark">✳</div>
              <strong>从一个想法开始</strong>
              <p>先讲好故事，再让画面发生。</p>
            </div>
            <button className="profile-button" onClick={() => setHelp(true)}>
              <span className="user-avatar">创</span>
              <span>
                创作者<small>连续短剧工作室</small>
              </span>
              <CircleHelp size={16} />
            </button>
          </div>
        </aside>
        {mobile && (
          <button
            className="sidebar-scrim"
            aria-label="关闭导航"
            onClick={() => setMobile(false)}
          />
        )}
        <div className="workspace-main">
          <header className="topbar">
            <div className="breadcrumbs">
              <button
                className="icon-button mobile-menu"
                aria-label="打开导航"
                onClick={() => setMobile(true)}
              >
                <Menu size={20} />
              </button>
              <button onClick={() => go('projects')}>工作室</button>
              <span>/</span>
              {seriesId && (
                <>
                  <span className="project-crumb">{project?.title ?? '加载中'}</span>
                  <span>/</span>
                </>
              )}
              <strong>{title}</strong>
            </div>
            <div className="topbar-actions">
              <span className={`saved-indicator ${connectionState}`}>
                <i />{' '}
                {connectionState === 'connected'
                  ? '已连接工作室'
                  : connectionState === 'connecting'
                    ? '正在连接服务'
                    : '连接中断 · 正在重连'}
              </span>
              <button className="icon-button" aria-label="帮助" onClick={() => setHelp(true)}>
                <CircleHelp size={18} />
              </button>
              <span className="avatar-small">创</span>
            </div>
          </header>
          <main>
            {projectsQuery.isError ? (
              <div className="error-banner">
                无法读取工作区。
                <button onClick={() => void projectsQuery.refetch()}>重新读取</button>
              </div>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
        {notice && (
          <div className={`toast ${notice.error ? 'error' : ''}`} role="status">
            {notice.error ? <CircleHelp size={17} /> : <Check size={17} />}
            <span>{notice.message}</span>
            <button onClick={() => setNotice(null)} aria-label="关闭通知">
              <X size={15} />
            </button>
          </div>
        )}
        {showCreate && (
          <CreateProject
            close={() => setShowCreate(false)}
            onCreated={(id) => {
              setShowCreate(false);
              navigate(`/w/${workspaceId}/series/${id}/studio`);
            }}
          />
        )}
        {branchDialog && branch && project && (
          <Dialog
            title="创建故事分支"
            subtitle="保留当前定稿，新分支可独立修改设定和制作剧集"
            close={() => setBranchDialog(false)}
          >
            <div className="dialog-body">
              <p>
                当前批准的故事与人设会冻结为新分支基线。新分支的剧集从第一集开始制作，原分支成片保留不变。
              </p>
              <Field label="分支名称">
                <input
                  maxLength={100}
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                  placeholder="例如：另一种结局"
                />
              </Field>
              <button
                className="button primary"
                disabled={busy || !branchName.trim()}
                onClick={() =>
                  void perform(async () => {
                    const b = await api<Schema<'Branch'>>(
                      `${base}/series/${project.id}/branches`,
                      'POST',
                      {
                        name: branchName,
                        sourceBranchId: branch.id,
                        sourceCanonRevision: branch.canonRevision,
                        expectedRevision: branch.revision,
                      },
                    );
                    selectBranch(b.id);
                    setBranchDialog(false);
                  }, '新分支已创建')
                }
              >
                创建分支
              </button>
            </div>
          </Dialog>
        )}
        {help && (
          <Dialog
            title="你的连续短剧工作室"
            subtitle="Dream · 连续短剧工作室"
            close={() => setHelp(false)}
          >
            <div className="dialog-body">
              <p>先创建系列，确定故事和人物。每一集默认一分钟，设定与定稿记录会留在项目中。</p>
              <div className="info-box">
                已接入真实数据库。模型是否可生成，以连接与能力验证状态为准；不会用模拟回复替代真实调用。
              </div>
              <button
                className="button secondary"
                onClick={() => {
                  setHelp(false);
                  void perform(async () => {
                    await api('/auth/logout', 'POST');
                    await qc.invalidateQueries({ queryKey: ['session'] });
                  });
                }}
              >
                <LogOut size={16} />
                退出工作室
              </button>
            </div>
          </Dialog>
        )}
      </div>
    </Context.Provider>
  );
}
function PageRoute() {
  const { view } = useParams();
  switch (view) {
    case 'novels':
    case 'novel':
      return <NovelPage />;
    case 'studio':
      return <StudioPage />;
    case 'episodes':
      return <EpisodesPage />;
    case 'production':
      return <ProductionPage />;
    case 'characters':
      return <CharactersPage />;
    case 'story':
      return <StoryPage />;
    case 'memory':
      return <MemoryPage />;
    case 'models':
      return <ModelsPage />;
    case 'jobs':
      return <JobsPage />;
    default:
      return <ProjectsPage />;
  }
}
function ProjectsPage() {
  const { projects, workspaceId, refresh, base } = useStudio();
  const access = useApi<Schema<'AccessProjection'>>(`${base}/access`);
  const canEdit = access.data?.actions.includes('edit') ?? false;
  const navigate = useNavigate();
  const [create, setCreate] = useState(false);
  const [deleting, setDeleting] = useState<Schema<'Series'> | null>(null);
  const [trash, setTrash] = useState(false);
  return (
    <div className="page projects-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">THE STORIES YOU'RE BUILDING</span>
          <h1>每个故事，都值得继续。</h1>
          <p>从灵感到第一集，再到下一集。你的创作在这里生长。</p>
        </div>
        <button className="button primary" onClick={() => setCreate(true)}>
          <Plus size={17} />
          新建系列
        </button>
      </div>
      <section className="welcome-banner">
        <div>
          <span className="pill-label">
            <Sparkles size={13} /> YOUR AI CREATIVE PARTNER
          </span>
          <h2>
            一个想法，
            <br />
            一整个新世界。
          </h2>
          <p>
            和 AI 一起讨论剧情、塑造人物，
            <br />
            把每一分钟，做成让人期待下一集的故事。
          </p>
          <button className="button ink" onClick={() => navigate(`/w/${workspaceId}/novels`)}>
            从小说开始改编 <ArrowUpRight size={17} />
          </button>
        </div>
        <MoonScene />
      </section>
      <div className="section-heading">
        <h2>
          我的系列 <span>{projects.length.toString().padStart(2, '0')}</span>
        </h2>
        <button className="button secondary" onClick={() => setTrash(true)}>
          <Trash2 size={16} />
          回收站
        </button>
      </div>
      <div className="project-grid">
        {projects.map((p, i) => (
          <article key={p.id} className="project-card project-card-manage">
            <button
              className="project-card-open"
              aria-label={`打开系列 ${p.title}`}
              onClick={() => navigate(`/w/${workspaceId}/series/${p.id}/studio`)}
            >
              <div className={`project-cover palette-${i % 3}`}>
                <span className="cover-grid" />
                <span className="cover-title">{p.title.slice(0, 8)}</span>
                <span className="cover-number">SERIES {(i + 1).toString().padStart(2, '0')}</span>
                <span className="cover-corner">
                  <ArrowUpRight size={20} />
                </span>
              </div>
              <div className="project-card-info">
                <h3>{p.title}</h3>
                <p>
                  <Badge>连续短剧</Badge>
                  <span>每集 {p.targetFormat.targetSeconds} 秒</span>
                </p>
              </div>
            </button>
            {canEdit && (
              <button
                className="project-delete"
                aria-label={`删除系列 ${p.title}`}
                title="删除系列"
                onClick={() => setDeleting(p)}
              >
                <Trash2 size={17} />
                <span>删除</span>
              </button>
            )}
          </article>
        ))}
        <button className="project-card add-card" onClick={() => setCreate(true)}>
          <span className="add-circle">
            <Plus size={25} />
          </span>
          <h3>留一页给新故事</h3>
          <p>一句话，就可以开始。</p>
        </button>
      </div>
      {deleting && <DeleteSeriesDialog series={deleting} close={() => setDeleting(null)} />}
      {trash && <SeriesTrashDialog close={() => setTrash(false)} canEdit={canEdit} />}
      {create && (
        <CreateProject
          close={() => setCreate(false)}
          onCreated={(id) => {
            setCreate(false);
            void refresh();
            navigate(`/w/${workspaceId}/series/${id}/studio`);
          }}
        />
      )}
    </div>
  );
}
function CreateProject({
  close,
  onCreated,
}: {
  close: () => void;
  onCreated: (id: string) => void;
}) {
  const { base, refresh } = useStudio();
  const [title, setTitle] = useState(''),
    [ratio, setRatio] = useState('9:16'),
    [seconds, setSeconds] = useState(60),
    [saving, setSaving] = useState(false),
    [error, setError] = useState('');
  return (
    <Dialog
      title="给故事一个名字"
      subtitle="先开始，世界观和细节可以和 AI 慢慢讨论。"
      close={close}
    >
      <form
        className="dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          setSaving(true);
          setError('');
          void api<Schema<'Series'>>(`${base}/series`, 'POST', {
            title: title.trim(),
            targetFormat: {
              targetSeconds: seconds,
              toleranceSeconds: 5,
              exactDuration: false,
              fpsNumerator: 24,
              fpsDenominator: 1,
              width: ratio === '9:16' ? 1080 : 1920,
              height: ratio === '9:16' ? 1920 : 1080,
            },
          } satisfies Schema<'SeriesCreate'>)
            .then(async (p) => {
              await refresh();
              onCreated(p.id);
            })
            .catch((e) => setError((e as Error).message))
            .finally(() => setSaving(false));
        }}
      >
        <Field label="系列名称">
          <input
            autoFocus
            placeholder="例如：月球来信"
            maxLength={100}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </Field>
        <div className="form-row">
          <Field label="单集目标时长" hint="默认允许上下浮动 5 秒">
            <div className="input-affix">
              <input
                type="number"
                min={10}
                max={600}
                value={seconds}
                onChange={(e) => setSeconds(Number(e.target.value))}
              />
              <span>秒 / 集</span>
            </div>
          </Field>
          <Field label="画面比例">
            <select value={ratio} onChange={(e) => setRatio(e.target.value)}>
              <option>9:16</option>
              <option>16:9</option>
            </select>
          </Field>
        </div>
        <div className="info-box">
          <Layers3 size={18} />
          <span>作为连续系列创建。后续剧集会复用人物设定，并接着前集的定稿继续。</span>
        </div>
        {error && <p className="form-error">{error}</p>}
        <footer className="dialog-footer">
          <button className="button secondary" type="button" onClick={close}>
            再想想
          </button>
          <button className="button primary" disabled={saving || !title.trim()}>
            {saving ? (
              <Spinner />
            ) : (
              <>
                创建系列 <ArrowRight size={16} />
              </>
            )}
          </button>
        </footer>
      </form>
    </Dialog>
  );
}
